import { signUpEmail } from '@/lib/auth-client';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@iq/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useRouter } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

export function SignUpForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (password.length < 12) {
      setError(t('auth.signUp.passwordTooShort'));
      return;
    }
    setPending(true);
    const result = await signUpEmail({ email, password, name });
    setPending(false);
    if (result.ok) {
      await queryClient.invalidateQueries({ queryKey: ['auth', 'session'] });
      await router.invalidate();
      navigate({ to: '/chat' });
    } else {
      setError(result.message);
    }
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>{t('auth.signUp.title')}</CardTitle>
        <CardDescription>{t('auth.signUp.subtitle')}</CardDescription>
      </CardHeader>
      <form onSubmit={onSubmit} data-testid="sign-up-form">
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="signup-name">{t('auth.signUp.name')}</Label>
            <Input
              id="signup-name"
              type="text"
              autoComplete="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="signup-email">{t('auth.signUp.email')}</Label>
            <Input
              id="signup-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="signup-password">{t('auth.signUp.password')}</Label>
            <Input
              id="signup-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">{t('auth.signUp.passwordHint')}</p>
          </div>
          {error ? (
            <p role="alert" className="text-sm text-destructive" data-testid="sign-up-error">
              {error}
            </p>
          ) : null}
        </CardContent>
        <CardFooter className="flex flex-col gap-3">
          <Button type="submit" disabled={pending} className="w-full">
            {pending ? t('auth.signUp.submitting') : t('auth.signUp.submit')}
          </Button>
          <p className="text-sm text-muted-foreground">
            {t('auth.signUp.haveAccount')}{' '}
            <Link to="/signin" className="underline">
              {t('auth.signUp.signInLink')}
            </Link>
          </p>
        </CardFooter>
      </form>
    </Card>
  );
}
